import {
  FC,
  KeyboardEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  LinearProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import { SearchDotsIcon } from '../../components/NavTiles/icons';
import Container from '../../components/Container';
import DocumentsViewToggle, {
  useDocumentsView,
} from '../../components/DocumentsViewToggle';
import Input from '../../components/Input';
import ListPagination, {
  DEFAULT_PAGE_SIZE,
} from '../../components/ListPagination';
import { FIELD_SX } from '../../components/fieldStyles';
import { colors, fontWeights, radii } from '../../theme/tokens';
import { SESSION_STORAGE_ARCHIVE_SUCCESS_KEY } from '../Archive/constants';
import {
  SESSION_STORAGE_ARCHIVE_EDIT_SUCCESS_KEY,
  SESSION_STORAGE_DOCUMENT_DELETED_KEY,
} from '../Document/constants';
import { searchDialogPath } from '../Search/paths';
import DocumentsTable from './DocumentsTable';
import DocumentGrid from './DocumentGrid';
import FilterFields from './FilterFields';
import MobileResultsTable from './MobileResultsTable';
import { useDocuments } from './useDocuments';
import { useDocumentActions, Notice } from './useDocumentActions';
import { useDocumentResolvers } from './useDocumentResolvers';
import { fullName } from './useFilterOptions';
import {
  DocumentFilters,
  EMPTY_FILTERS,
  FilterOption,
} from './types';

// XD screen 2 ("2. Home" in DESIGN.md): header and tiles (Container),
// filter row and documents table.
//
// It replaces MyDocuments and AllDocuments, which were the same screen
// twice. The XD has a single Home with an "Archiveur" filter; "my
// documents" is that Home with the archiver preselected, and it is what
// `/my-documents` (`scope="me"`) enters with. The visibility of the confidential ones
// is narrowed by the backend (`listingScope`).
//
// Decisions where the XD or the API give no data:
//   - "Archiveur": `GET /api/users/` is `requireAdmin`. A USER sees the
//     dropdown with "Tous" and "Moi", which is what the API lets them query
//     (see useFilterOptions and useDocuments).
//   - "Archiveur" column: `GET /api/archives/documents` does not return
//     `archiver`; the cell shows "—" until the backend adds it. With the
//     filter set (`/:id/my-documents`) it does arrive and is resolved.
//   - Edit/delete: only for the administrator or the archiver. When the
//     API does not say who archived it, the action is not offered.
//   - Dates: a range per column (Émission, Archivage), as the XD draws and
//     the API now filters by.
//   - "Nom du fichier": the listing does not bring `file_path`; `title` is shown.
//   - Padlock next to the name of the confidential ones: it is not in the XD, but
//     the previous listing had it and it is information the user needs.
//   - Search box icon "Que cherchez-vous ?": opens the Recherche modal
//     (screen 6) with what is typed as "Mots clés"; Enter is still the
//     simple search over this list.
//   - List/grid switch at the end of the filter row ("Flux 2" revision,
//     DESIGN.md): the grid is the official document card, shared with
//     Résultats along with the stored preference.
//   - Empty/loading/error states (gap 8): in DocumentsTable/MobileResultsTable.
//   - Mobile: full-width search box + "Filtres" that opens a bottom sheet;
//     applied filters as chips below the search box. LTU asked (feedback on
//     the mobile Home, overriding the smartphone XD's tile-only hub) to keep
//     the document list here, styled like Résultats' table
//     (MobileResultsTable) rather than as cards, and to tighten the tiles
//     and header so the list fits with less scrolling (NavTiles, Container).

export type HomeScope = 'all' | 'me';

type Props = {
  /** With `me`, "Archiveur" enters preselected with the current user. */
  scope?: HomeScope;
  /**
   * Hide the navigation tiles. The routes that open a modal over this Home
   * (/archive, /search, /profile) pass it: the XD modal artboards show the
   * header over a page without tiles.
   */
  hideTiles?: boolean;
  /**
   * What is painted over the list: the "Ajouter un document" modal on
   * "/archive" (pages/Archive) or the Recherche one on "/search". The Home does
   * not know what it is; that way those screens can import from here without a cycle.
   */
  children?: ReactNode;
};

// The notices other screens leave in sessionStorage so that the list
// shows them on return (archiving, editing and deletion from the record). They are only read here: the deletion
// goes in an effect, because StrictMode runs the state initializer
// twice and would keep the result of the second read.
const PENDING_NOTICE_KEYS = [
  SESSION_STORAGE_ARCHIVE_EDIT_SUCCESS_KEY,
  SESSION_STORAGE_ARCHIVE_SUCCESS_KEY,
  SESSION_STORAGE_DOCUMENT_DELETED_KEY,
];

const peekPendingNotice = (): Notice | undefined => {
  if (sessionStorage.getItem(SESSION_STORAGE_DOCUMENT_DELETED_KEY)) {
    return { severity: 'success', text: 'Document supprimé.' };
  }
  if (sessionStorage.getItem(SESSION_STORAGE_ARCHIVE_EDIT_SUCCESS_KEY)) {
    return { severity: 'success', text: 'Document mis à jour avec succès.' };
  }
  if (sessionStorage.getItem(SESSION_STORAGE_ARCHIVE_SUCCESS_KEY)) {
    return { severity: 'success', text: 'Archivé avec succès.' };
  }
  return undefined;
};

const Home: FC<Props> = ({ scope = 'all', hideTiles = false, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('tablet'));
  const navigate = useNavigate();
  const location = useLocation();
  const { myId, users, services, userName, resolvers } =
    useDocumentResolvers();

  const initialFilters = (): DocumentFilters => ({
    ...EMPTY_FILTERS,
    archiver: scope === 'me' ? myId : '',
  });

  const [filters, setFilters] = useState<DocumentFilters>(initialFilters);
  const [searchDraft, setSearchDraft] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetDraft, setSheetDraft] = useState<DocumentFilters>(EMPTY_FILTERS);
  // "Flux 2" puts the list/grid switch on the Home too; the preference is
  // shared with Résultats (DocumentsViewToggle).
  const [view, changeView] = useDocumentsView();

  useEffect(() => {
    PENDING_NOTICE_KEYS.forEach((key) => sessionStorage.removeItem(key));
  }, []);

  useEffect(() => {
    document.title =
      scope === 'me' ? 'ArchivOs - Mes documents' : 'ArchivOs - Accueil';
  }, [scope]);

  // `/my-documents` and `/all-documents` mount this same component, and the
  // router reuses it when moving from one to the other: the route change resets
  // the filters here, not on mount.
  useEffect(() => {
    setFilters(initialFilters());
    setSearchDraft('');
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, myId]);

  const { data, isLoading, isFetching, isError, refetch } = useDocuments({
    filters,
    page,
    pageSize,
  });

  const documents = data?.documents ?? [];
  const total = data?.totalItems ?? 0;

  const { onAction, elements: actionElements } = useDocumentActions({
    initialNotice: peekPendingNotice(),
    onDeleted: () => {
      // Last row of a page that is not the first: step back, or the
      // page would be left empty.
      if (documents.length === 1 && page > 0) {
        setPage(page - 1);
      } else {
        refetch();
      }
    },
    onVanished: () => refetch(),
  });

  // --- Dropdown options -------------------------------------------------

  const archiverOptions = useMemo<FilterOption[]>(() => {
    const options: FilterOption[] = [{ value: '', label: 'Tous' }];
    if (myId) options.push({ value: myId, label: 'Moi' });

    users
      .filter((user) => user.id && user.id !== myId)
      .map((user) => ({ value: user.id as string, label: fullName(user) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'fr'))
      .forEach((option) => options.push(option));

    return options;
  }, [users, myId]);

  const serviceOptions = useMemo<FilterOption[]>(
    () => [
      { value: '', label: 'Tous' },
      ...services
        .map((service) => ({ value: service.id, label: service.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
    ],
    [services],
  );

  // --- Filters ----------------------------------------------------------

  const applyFilters = (next: DocumentFilters) => {
    setFilters(next);
    setPage(0);
  };

  const submitSearch = () => {
    const search = searchDraft.trim();
    if (search !== filters.search) applyFilters({ ...filters, search });
  };

  const onSearchKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      submitSearch();
    }
  };

  // The Recherche modal, with what is typed as "Mots clés" and this route as
  // the one to return to when closing it.
  const openAdvancedSearch = () => {
    navigate(searchDialogPath(searchDraft), {
      state: { from: `${location.pathname}${location.search}` },
    });
  };

  const hasFilters =
    Boolean(filters.search) ||
    Boolean(filters.archiver) ||
    Boolean(filters.service) ||
    filters.issuingDateFrom !== null ||
    filters.issuingDateTo !== null ||
    filters.archivingDateFrom !== null ||
    filters.archivingDateTo !== null;

  // A range chip covers whichever bounds are set: both ("13/02 – 20/03"), or
  // just the open side ("à partir du…" / "jusqu'au…") when only one is.
  const rangeChipLabel = (
    name: string,
    from: DocumentFilters['issuingDateFrom'],
    to: DocumentFilters['issuingDateTo'],
  ): string | null => {
    if (from && to) {
      return `${name} : ${from.format('DD/MM/YYYY')} – ${to.format('DD/MM/YYYY')}`;
    }
    if (from) return `${name} : à partir du ${from.format('DD/MM/YYYY')}`;
    if (to) return `${name} : jusqu'au ${to.format('DD/MM/YYYY')}`;
    return null;
  };

  // The applied filters, as removable chips (mobile). The search box does
  // not enter: it is seen in its own field. A date chip stands for both
  // bounds of its range together — removing it clears whichever of the two
  // is set, not just one side.
  type ChipKey = 'archiver' | 'service' | 'issuingDate' | 'archivingDate';
  const activeChips: Array<{ key: ChipKey; label: string }> = [];
  if (filters.archiver) {
    activeChips.push({
      key: 'archiver',
      label: `Archiveur : ${
        archiverOptions.find((o) => o.value === filters.archiver)?.label ??
        userName(filters.archiver)
      }`,
    });
  }
  if (filters.service) {
    activeChips.push({
      key: 'service',
      label: `Service : ${
        serviceOptions.find((o) => o.value === filters.service)?.label ??
        filters.service
      }`,
    });
  }
  const issuingLabel = rangeChipLabel(
    'Emission',
    filters.issuingDateFrom,
    filters.issuingDateTo,
  );
  if (issuingLabel) activeChips.push({ key: 'issuingDate', label: issuingLabel });

  const archivingLabel = rangeChipLabel(
    'Archivage',
    filters.archivingDateFrom,
    filters.archivingDateTo,
  );
  if (archivingLabel) {
    activeChips.push({ key: 'archivingDate', label: archivingLabel });
  }

  const clearChip = (key: ChipKey) => {
    if (key === 'issuingDate') {
      applyFilters({ ...filters, issuingDateFrom: null, issuingDateTo: null });
    } else if (key === 'archivingDate') {
      applyFilters({ ...filters, archivingDateFrom: null, archivingDateTo: null });
    } else {
      applyFilters({ ...filters, [key]: EMPTY_FILTERS[key] });
    }
  };

  const openSheet = () => {
    setSheetDraft(filters);
    setSheetOpen(true);
  };

  const applySheet = () => {
    applyFilters({ ...sheetDraft, search: filters.search });
    setSheetOpen(false);
  };

  const clearSheet = () => {
    setSheetDraft({ ...EMPTY_FILTERS, search: filters.search });
  };

  // --- Render -----------------------------------------------------------

  const searchField = (
    <Input
      placeholder="Que cherchez-vous ?"
      variant="outlined"
      size="small"
      value={searchDraft}
      onChange={(e) => setSearchDraft(e.target.value)}
      onKeyDown={onSearchKeyDown}
      inputProps={{ 'aria-label': 'Recherche' }}
      sx={{ ...(FIELD_SX as object), flex: '1 1 260px', minWidth: 0 }}
      slotProps={{
        input: {
          endAdornment: (
            <InputAdornment position="end">
              {/* XD: the same dots-and-magnifier glyph as the tile, in blue. */}
              <IconButton
                aria-label="Recherche détaillée"
                edge="end"
                onClick={openAdvancedSearch}
                sx={{ color: colors.primary }}
              >
                <SearchDotsIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ),
        },
      }}
    />
  );

  const pagination = (
    <ListPagination
      count={total}
      page={page}
      pageSize={pageSize}
      onPageChange={setPage}
      onPageSizeChange={(size) => {
        setPageSize(size);
        setPage(0);
      }}
    />
  );

  return (
    <Container showTiles={!hideTiles} plain>
      <Box>
        {/* Filter row */}
        {isMobile ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {searchField}
              <Badge
                badgeContent={activeChips.length}
                color="secondary"
                overlap="rectangular"
              >
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FilterListIcon />}
                  onClick={openSheet}
                  sx={{ px: 2, py: 0.75, whiteSpace: 'nowrap' }}
                >
                  Filtres
                </Button>
              </Badge>
            </Box>
            {activeChips.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {activeChips.map((chip) => (
                  <Chip
                    key={chip.key}
                    label={chip.label}
                    variant="outlined"
                    onDelete={() => clearChip(chip.key)}
                  />
                ))}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              // Room above the floating date labels (see FilterFields).
              pt: 2.5,
              columnGap: 1.5,
              rowGap: 3,
            }}
          >
            {searchField}
            <FilterFields
              value={filters}
              onChange={(patch) => applyFilters({ ...filters, ...patch })}
              archiverOptions={archiverOptions}
              serviceOptions={serviceOptions}
            />
            <Box sx={{ ml: 'auto' }}>
              <DocumentsViewToggle view={view} onChange={changeView} />
            </Box>
          </Box>
        )}

        {/* Progress when changing page or filter, without emptying the table */}
        <Box
          sx={(theme) => ({
            height: 4,
            mt: 2,
            [theme.breakpoints.down('tablet')]: { mt: 1 },
          })}
        >
          {isFetching && !isLoading && (
            <LinearProgress color="secondary" sx={{ height: 2 }} />
          )}
        </Box>

        {/* Documents, as the XD table, the "Flux 2" grid, or — on mobile —
            the Résultats-style table LTU asked to keep here. */}
        {isMobile ? (
          <MobileResultsTable
            documents={documents}
            isLoading={isLoading}
            isError={isError}
            hasFilters={hasFilters}
            resolvers={resolvers}
            onAction={onAction}
            onRetry={() => refetch()}
          />
        ) : view === 'grid' ? (
          <DocumentGrid
            documents={documents}
            isLoading={isLoading}
            isError={isError}
            resolvers={resolvers}
            onAction={onAction}
            onRetry={() => refetch()}
          />
        ) : (
          <DocumentsTable
            documents={documents}
            isLoading={isLoading}
            isError={isError}
            hasFilters={hasFilters}
            resolvers={resolvers}
            onAction={onAction}
            onRetry={() => refetch()}
          />
        )}

        {pagination}
      </Box>

      {/* Bottom filter sheet (mobile) */}
      <Drawer
        anchor="bottom"
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: `${radii.card * 2}px`,
            borderTopRightRadius: `${radii.card * 2}px`,
            backgroundColor: colors.white,
            maxHeight: '85vh',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Typography
              component="h2"
              sx={{ fontSize: '1.125rem', fontWeight: fontWeights.semiBold }}
            >
              Filtres
            </Typography>
            <IconButton
              aria-label="Fermer"
              onClick={() => setSheetOpen(false)}
              sx={{ color: colors.textBase }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <FilterFields
            value={sheetDraft}
            onChange={(patch) => setSheetDraft({ ...sheetDraft, ...patch })}
            archiverOptions={archiverOptions}
            serviceOptions={serviceOptions}
            stacked
          />

          <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={clearSheet}
              sx={{ flex: 1 }}
            >
              Effacer
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={applySheet}
              sx={{ flex: 1 }}
            >
              Appliquer
            </Button>
          </Box>
        </Box>
      </Drawer>

      {actionElements}

      {children}
    </Container>
  );
};

export default Home;
